import { getApiService } from "@/services/apiService";
import { useAuth } from "@/zustand/Zustand";
import { Sentry } from '@/helpers/sentry';

const LEGACY_KEY_PREFIX = "fp-tour-seen";

export function isTourSeen(
  seenTours: Record<string, number> | undefined,
  tourId: string,
  version: number
): boolean {
  if (!seenTours) return false;
  const seenVersion = seenTours[tourId];
  return typeof seenVersion === "number" && seenVersion >= version;
}

export async function markTourSeen(tourId: string, version: number): Promise<void> {
  const { userData, setUserData } = useAuth.getState();
  if (userData) {
    const existing = userData.seenTours?.[tourId] ?? 0;
    if (version > existing) {
      setUserData({
        ...userData,
        seenTours: { ...(userData.seenTours ?? {}), [tourId]: version },
      });
    }
  }
  try {
    const api = getApiService();
    await api.post("/shared/users/tours/mark-seen", JSON.stringify({ tourId, version }));
  } catch (error) {
    Sentry.logger.warn("Failed to mark tour seen", { error });
  }
}

export async function resetTourSeen(tourId: string): Promise<void> {
  const { userData, setUserData } = useAuth.getState();
  if (userData?.seenTours && tourId in userData.seenTours) {
    const next = { ...userData.seenTours };
    delete next[tourId];
    setUserData({ ...userData, seenTours: next });
  }
  try {
    const api = getApiService();
    await api.post("/shared/users/tours/reset", JSON.stringify({ tourId }));
  } catch (error) {
    Sentry.logger.warn("Failed to reset tour", { error });
  }
}

// One-shot migration from the old localStorage scheme (`fp-tour-seen:<userId>:<tourId>` → version).
// Reads any matching keys for the current user, POSTs them to the backend, then clears them.
export async function migrateLocalStorageTours(userId: string): Promise<void> {
  const prefix = `${LEGACY_KEY_PREFIX}:${userId}:`;
  const collected: Record<string, number> = {};
  const keysToRemove: string[] = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const tourId = key.slice(prefix.length);
      const raw = localStorage.getItem(key);
      const version = raw ? Number(raw) : NaN;
      if (Number.isFinite(version)) {
        collected[tourId] = version;
        keysToRemove.push(key);
      }
    }
  } catch {
    return;
  }

  if (Object.keys(collected).length === 0) return;

  try {
    const api = getApiService();
    const res = await api.post(
      "/shared/users/tours/migrate",
      JSON.stringify({ tours: collected })
    );
    if (!res.ok) return;

    const { userData, setUserData } = useAuth.getState();
    if (userData) {
      const merged = { ...(userData.seenTours ?? {}) };
      for (const [tourId, version] of Object.entries(collected)) {
        const existing = merged[tourId] ?? 0;
        if (version > existing) merged[tourId] = version;
      }
      setUserData({ ...userData, seenTours: merged });
    }

    for (const key of keysToRemove) {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
    }
  } catch (error) {
    Sentry.logger.warn("Failed to migrate tours", { error });
  }
}
